import { X } from 'lucide-react';
import * as React from 'react';

import { DimeButton } from '../DimeButton.js';
import { DimeSearchInput } from '../DimeSearchInput.js';
import { DataTableViewOptions } from './DataTableViewOptions.js';
import type { ToolbarTable } from './toolbar-table.js';

export interface DataTableToolbarProps {
  table: ToolbarTable;
  searchColumn?: string;
  searchPlaceholder?: string;
  searchMaxLength?: number;
  onSearchChange?: (value: string) => void;
  // Controls the server-search input when set alongside onSearchChange; omitted, the input keeps its own state.
  searchValue?: string;
  filters?: React.ReactNode;
  actions?: React.ReactNode;
  showViewOptions?: boolean;
  viewOptionsMenuClassName?: string;
  filterChips?: React.ReactNode;
  // Built by DimeDataTable so this stays non-generic and DimeNestedTable can share it.
  exportControl?: React.ReactNode;
  // Card mode drops the header row, so DimeDataTable passes the sort buttons here instead.
  sortControl?: React.ReactNode;
}

export function DataTableToolbar({
  table,
  searchColumn,
  searchPlaceholder,
  searchMaxLength,
  onSearchChange,
  searchValue,
  filters,
  actions,
  showViewOptions,
  viewOptionsMenuClassName,
  filterChips,
  exportControl,
  sortControl,
}: Readonly<DataTableToolbarProps>) {
  const searchCol = searchColumn ? table.getColumn(searchColumn) : undefined;
  const [internalServerSearch, setInternalServerSearch] = React.useState('');
  const serverSearch = searchValue ?? internalServerSearch;
  // onSearchChange is server-side search, not a column filter; either one shows the input.
  const showSearchInput = Boolean(searchColumn || onSearchChange);
  const isFiltered = onSearchChange ? serverSearch !== '' : table.state.columnFilters.length > 0;

  function handleSearchChange(value: string) {
    if (onSearchChange) {
      if (searchValue === undefined) setInternalServerSearch(value);
      onSearchChange(value);
    } else {
      searchCol?.setFilterValue(value);
    }
  }

  const inputValue = onSearchChange
    ? serverSearch
    : ((searchCol?.getFilterValue() as string) ?? '');

  return (
    <div className="pb-3">
      {/* Below sm the left group flattens so search takes the first row and every control wraps under it. */}
      <div
        data-slot="data-table-toolbar"
        className="flex items-center justify-between gap-2 max-sm:flex-wrap"
      >
        <div className="flex flex-1 flex-wrap items-center gap-2 max-sm:contents">
          {showSearchInput ? (
            <DimeSearchInput
              className="min-w-40 max-w-sm flex-1 max-sm:max-w-none max-sm:basis-full"
              placeholder={searchPlaceholder ?? 'Search...'}
              aria-label={searchPlaceholder ?? 'Search'}
              maxLength={searchMaxLength}
              value={inputValue}
              onValueChange={handleSearchChange}
              clearable={isFiltered}
              onClear={() => {
                handleSearchChange('');
                if (!onSearchChange) table.resetColumnFilters();
              }}
            />
          ) : null}
          {filters}
          {isFiltered && !showSearchInput ? (
            <DimeButton
              type="button"
              variant="ghost"
              size="sm"
              className="gap-1.5"
              onClick={() => table.resetColumnFilters()}
            >
              Reset
              <X className="size-4" />
            </DimeButton>
          ) : null}
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2 max-sm:ml-auto">
          {sortControl}
          {actions}
          {showViewOptions ? (
            <DataTableViewOptions table={table} menuClassName={viewOptionsMenuClassName} />
          ) : null}
          {exportControl}
        </div>
      </div>
      {filterChips && (
        // Scrolls rather than wraps: a long row costs one line at any width, and each chip keeps
        // its size instead of compressing until its label truncates.
        <div className="no-scrollbar scroll-fade-x mt-2 flex flex-nowrap items-center gap-2 overflow-x-auto [&>*]:shrink-0">
          {filterChips}
        </div>
      )}
    </div>
  );
}
